import type {Readable} from 'svelte/store';
import type {RecordRow} from 'src/features/record-grid/lib/types';
import type {Rec, UUID} from 'typings/portaro.be.types';
import type {ProjectType} from './types';
import type {ProjectReportOptimisationService} from './project-report-optimisation.service';
import {writable} from 'svelte/store';
import {createContext, getContextOrThrow} from 'src/core';
import {FOND_PROJECT} from 'src/features/erp-sutor/sutor-fonds';

const projectReportOptimisationContextKey = Symbol('project-report-optimisation-ctx');

interface ProjectReportOptimisationContext {
    service: ProjectReportOptimisationService;
    projectRecord: Rec;
    projectType: ProjectType;
    openedUser$: Readable<RecordRow | null>;
    optimisedBusinessItemRecord$: Readable<RecordRow | null>;
    usedReportsForOptimisation$: Readable<UUID[]>;
    openOverviewSignal$: Readable<RecordRow | null>;
    setOpenedUser: (workReport: RecordRow | null) => void;
    addUsedReportForOptimisation: (reportId: UUID) => void;
    setOptimisedBusinessItemRecord: (businessItemRecord: RecordRow | null) => void;
    setOpenOverviewSignal: (record: RecordRow | null) => void;
}

export function createProjectReportOptimisationContext(projectRecord: Rec, service: ProjectReportOptimisationService): ProjectReportOptimisationContext {
    const projectType = projectRecord.fond.id === FOND_PROJECT.REPORTED.fond ? 'reported' : 'contracted';
    const openedUser$ = writable<RecordRow | null>(null);
    const usedReportsForOptimisation$ = writable<UUID[]>([]);
    const openOverviewSignal$ = writable<RecordRow | null>(null);
    const optimisedBusinessItemRecord$ = writable<RecordRow | null>(null);

    return createContext(projectReportOptimisationContextKey, {
        projectRecord,
        projectType,
        service,
        openedUser$,
        openOverviewSignal$,
        usedReportsForOptimisation$,
        optimisedBusinessItemRecord$,
        setOpenedUser: (workReport) => openedUser$.set(workReport),
        addUsedReportForOptimisation: (reportId) => usedReportsForOptimisation$.update((usedReports) => [...usedReports, reportId]),
        setOptimisedBusinessItemRecord: (businessItemRecord) => optimisedBusinessItemRecord$.set(businessItemRecord),
        setOpenOverviewSignal: (record) => openOverviewSignal$.set(record)
    });
}

export function getProjectReportOptimisationContext(): ProjectReportOptimisationContext {
    return getContextOrThrow(projectReportOptimisationContextKey);
}